import { lazy, Suspense, useMemo } from 'react'
import type { CommandAction } from '../components/CommandPalette'
import LogoutConfirmDialog from '../components/LogoutConfirmDialog'
import { buildCommandActions } from '../components/app/commandActions'
import { useCommandShortcuts } from '../components/app/useCommandShortcuts'
import useSetupWizard from '../hooks/admin/useSetupWizard'
import { useAdminAppEffects } from '../hooks/app/useAdminAppEffects'
import { useAdminAppUiActions } from '../hooks/app/useAdminAppUiActions'
import useAdminApp from '../hooks/useAdminApp'
import { useToast } from '../hooks/useToast'
import { useI18n } from '../i18n'
import { useAdminThemeRuntime } from '../theme/useAdminThemeRuntime'
import { getUserCapabilities } from '../utils/permissions'
import { AppContentMainView } from './AppContentMainView'
import { useAppViewState } from './useAppViewState'
import { useReleaseNotesDialog } from './useReleaseNotesDialog'
import { useSetupEntryNavigation } from './useSetupEntryNavigation'

const AdminShell = lazy(() => import('../components/AdminShell'))
const AuthScreen = lazy(() => import('../components/AuthScreen'))
const SetupEntryScreen = lazy(() => import('../components/SetupEntryScreen'))
const SetupWizard = lazy(() => import('../components/SetupWizard'))
const CommandPalette = lazy(() => import('../components/CommandPalette'))
const ReleaseNotesModal = lazy(() => import('../components/ReleaseNotesModal'))

function AppContent() {
  const app = useAdminApp()
  useAdminThemeRuntime()
  const accountRoles = app.account?.roles ?? []
  const customCapabilities = app.profile?.customCapabilities ?? []
  const resolvedCapabilities = getUserCapabilities(
    accountRoles,
    app.roleDefinitions ?? [],
    customCapabilities,
    app.account?.capabilities ?? [],
  )
  const canReadReleaseNotes =
    resolvedCapabilities.includes('*') || resolvedCapabilities.includes('manage_security_settings')

  const setup = useSetupWizard()
  const { setupEntryIntent, navigateSetupEntry } = useSetupEntryNavigation()
  const { setLanguage, language, t } = useI18n()
  const { showToast } = useToast()

  const {
    commandOpen,
    logoutDialogOpen,
    handleCommandOpenChange,
    handleLogout,
    confirmLogout,
    closeLogoutDialog,
    handleLoginWithToast,
    runPasskeyAuthWithToast,
    setCommandOpenState,
  } = useAdminAppUiActions({
    app,
    showToast,
    t,
  })

  const actions = useMemo<CommandAction[]>(
    () =>
      buildCommandActions(t, app.setSection, confirmLogout, {
        isCustomerOnly:
          app.account?.roles?.includes('customer') === true &&
          !app.account?.roles?.some((role) => role === 'admin' || role === 'super-admin' || role === 'site-manager'),
        userCapabilities: resolvedCapabilities,
      }),
    [app.account?.roles, app.setSection, confirmLogout, resolvedCapabilities, t],
  )

  useCommandShortcuts(actions, app.isAuthenticated, setCommandOpenState)

  useAdminAppEffects({
    app,
    setupStatus: setup.setupStatus,
    setupLoading: setup.setupLoading,
    refreshStatus: setup.refreshStatus,
    logout: app.logout,
    language,
    setLanguage,
  })

  const { mode, checkingSetup, shouldShowSetup } = useAppViewState({
    isAuthenticated: app.isAuthenticated,
    setupStatus: setup.setupStatus,
    setupLoading: setup.setupLoading,
    sessionBootstrapLoading: app.sessionBootstrapLoading,
    forceSetupWizardVisible: setup.setupSuccessVisible,
    setupEntryIntent,
  })

  const { releaseNotesDialog, acknowledgeReleaseNotes } = useReleaseNotesDialog({
    app,
    canReadReleaseNotes,
    shouldShowSetup,
    checkingSetup,
  })

  return (
    <>
      <AppContentMainView
        mode={mode}
        app={app}
        setup={setup}
        handleOpenSetupEntry={() => navigateSetupEntry('setup')}
        handleCommandOpenChange={handleCommandOpenChange}
        confirmLogout={confirmLogout}
        handleLoginWithToast={handleLoginWithToast}
        runPasskeyAuthWithToast={runPasskeyAuthWithToast}
        AdminShell={AdminShell}
        SetupEntryScreen={SetupEntryScreen}
        SetupWizard={SetupWizard}
        AuthScreen={AuthScreen}
      />
      {app.isAuthenticated && !shouldShowSetup && !checkingSetup && (
        <Suspense fallback={null}>
          <CommandPalette
            open={commandOpen}
            onOpenChange={handleCommandOpenChange}
            actions={actions}
            resolveQueryActions={async () => []}
          />
        </Suspense>
      )}
      <LogoutConfirmDialog
        open={logoutDialogOpen}
        onConfirm={handleLogout}
        onClose={closeLogoutDialog}
        title={t('auth.logoutConfirm')}
        description={t('auth.logoutConfirmMessage')}
      />
      {releaseNotesDialog.open || releaseNotesDialog.currentRelease ? (
        <Suspense fallback={null}>
          <ReleaseNotesModal
            open={releaseNotesDialog.open}
            currentRelease={releaseNotesDialog.currentRelease}
            previousRelease={releaseNotesDialog.previousRelease}
            notesHtml={releaseNotesDialog.notesHtml}
            onAcknowledge={acknowledgeReleaseNotes}
          />
        </Suspense>
      ) : null}
    </>
  )
}

export default AppContent
